import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {usePageOffsetInPopupLayer} from '@Hooks/poster-editor/usePageOffsetInPopupLayer';
import {getTextboxLineTopOffset} from '@Components/poster-editor/components/text-item-popup/text-item-popup.library';
import {ControlledListDividerItem} from '@Components/controlled-list/components/controlled-list-divider-item';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {v4 as uuidv4} from 'uuid';
import {updateSpellCheckPopUpOpenState, updateTextSelectionPopUpOpenState} from '@Components/poster-editor/components/text-item-popup/text-item-popup-slice';
import {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import {addToDictionary, updateSpellCheckCache} from '@Libraries/spell-check-library';
import {SpellCheckSettingsButton} from '@Components/spell-check-ignore-button';
import {degreesToRadians} from '@Utils/math.util';
import {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {replaceTextboxRangeWithText, replaceTextboxSelectionWithText} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';
import {updateSpellCheckSettingsState} from '@Components/poster-editor/poster-editor-reducer';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {copyToClipboard} from '@Utils/clipboard.util';
import {Canvas, Textbox} from '@postermywall/fabricjs-2';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './text-item-popup.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {getIsSpellCheckEnabled} from '@/reducers/user-preferences.reducer';

interface IndexLocation {
  lineIndex: number;
  charIndex: number;
}

interface PositionalParams {
  left?: number;
  top?: number;
}

interface CornerPoint {
  x: number;
  y: number;
}

const CORNER_PADDING = 5;
const SUGGESTION_POPUP_MAX_WIDTH = 200;
const SUGGESTION_POPUP_HEIGHT = 110;
const SELECTION_POPUP_HEIGHT = 48;
const SELECTION_POPUP_EXPANDED_HEIGHT = 100;

export function TextItemPopup(): ReactElement<HTMLElement> | null {
  const popupState = useAppSelector((state) => {
    return state.textItemPopUpMenu;
  });
  const isSpellcheckEnabled = useAppSelector(getIsSpellCheckEnabled);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeCanvas = currentPage?.fabricCanvas;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [isTextOptionExpanded, setIsTextOptionExpanded] = useState(false);

  useEffect(() => {
    if (!isSpellcheckEnabled) {
      closeSpellCheckPopup();
    }
  }, [isSpellcheckEnabled]);

  const popUpRef = useRef<HTMLDivElement>(null);
  const popupLayerRef = useRef<HTMLDivElement>(null);
  const currentPageIdForPopup = useAppSelector((state) => state.posterEditor.currentPageId);
  const pageOffsetInPopupLayer = usePageOffsetInPopupLayer(popupLayerRef, currentPageIdForPopup, popupState.openSpellCheckPopUpMenu || popupState.openTextSelectionPopupMenu);
  const dispatch = useAppDispatch();

  const closeSpellCheckPopup = (): void => {
    dispatch(updateSpellCheckSettingsState(false));
    dispatch(updateSpellCheckPopUpOpenState(false));
  };

  if (!(activeCanvas instanceof Canvas)) {
    return null;
  }

  const closeTextSelectionPopup = (): void => {
    activeCanvas?.requestRenderAll();
    dispatch(updateTextSelectionPopUpOpenState(false));
    setIsTextOptionExpanded(false);
  };

  const applyFocusOnTextarea = (): void => {
    const activeObject = activeCanvas?.getActiveObject();
    const editingTextbox = activeItem?.isText() || activeItem?.isSlideshow() ? activeItem.getEditingTextbox() : null;
    if (editingTextbox?.hiddenTextarea && activeCanvas && activeObject && activeObject instanceof Textbox) {
      editingTextbox.hiddenTextarea.focus();
    }
  };

  const getSuggestions = (): ReactElement<HTMLElement>[] | null => {
    const suggestedWordsReactComponent: ReactElement<HTMLElement>[] = [];

    if (popupState.words) {
      for (const word of popupState.words) {
        const id = uuidv4();
        suggestedWordsReactComponent.push(
          <Button size={ButtonSize.LARGE}
            key={id}
            text={word}
            customClasses={styles.suggestedWordsButton}
            textClasses={`body-lg-bold ${styles.suggestedWords}`}
            type={ButtonType.TERTIARY}
            onClick={onSuggestionClicked}
          />
        );
      }
      return suggestedWordsReactComponent;
    }
    return null;
  };

  const getSuggestionsElement = (): ReactElement<HTMLElement> | null => {
    if (popupState.words?.length) {
      return (
        <li id="popup-btn-suggestions" className="spacing-p-0">
          <div className={`${styles.suggestions} flex-items-center flex-h-row`}>{getSuggestions()}</div>
          <ul>
            <ControlledListDividerItem id="spellCheckDivider" />
          </ul>
        </li>
      );
    }
    return null;
  };

  const onSuggestionClicked = (e: React.MouseEvent<HTMLElement>): void => {
    const target = e.target as HTMLElement;
    const start = popupState.wordPosition?.start;
    const end = popupState.wordPosition?.end;
    const selectedItem = window.posterEditor?.whiteboard?.getSelectedItems()?.[0];

    if (typeof start === 'number' && typeof end === 'number' && selectedItem && (selectedItem instanceof TextItem || selectedItem instanceof SlideshowItem)) {
      const activeObjects = window.posterEditor?.whiteboard?.getSelectedViews();
      if (activeObjects) {
        const suggestion = target.innerText;
        const editingTextbox = selectedItem.getEditingTextbox();
        if (editingTextbox) {
          // The replacement goes through fabric's editing API so the per-character styles map
          // shifts with the text (word positions are fabric grapheme indices, the same units
          // the spell checker styled the word with).
          replaceTextboxRangeWithText(editingTextbox, suggestion, start, end);
          selectedItem.onCanvasTextChange(editingTextbox.text);
        } else {
          const prevText = selectedItem.getActiveText();
          selectedItem.updateActiveText(prevText.substring(0, start) + suggestion + prevText.substring(end, prevText.length));
        }
        updateSpellCheckCache(suggestion, true);
        applyFocusOnTextarea();
        closeSpellCheckPopup();
      }
    } else if (!selectedItem) {
      closeSpellCheckPopup();
    }
  };

  const onIgnore = async (): Promise<void> => {
    const start = popupState.wordPosition?.start;
    const end = popupState.wordPosition?.end;

    const selectedItem = window.posterEditor?.whiteboard?.getSelectedItems()?.[0];
    if (typeof start === 'number' && typeof end === 'number' && selectedItem && (selectedItem.isText() || selectedItem.isSlideshow())) {
      const activeObjects = window.posterEditor?.whiteboard?.getSelectedViews();
      if (activeObjects) {
        const text = selectedItem.getActiveText().substring(start, end).replace(/[0-9]/g, ' ').trim();
        if (text) {
          await addToDictionary(text);
          updateSpellCheckCache(text, true);
          applyFocusOnTextarea();
          const fabricTextbox = activeObjects[0];
          await selectedItem?.page?.spellCheck?.checkSpell(fabricTextbox as Textbox);
        }
      }
    }
    closeSpellCheckPopup();
  };

  /**
   * Scene-space top-left of the textbox. A grouped object's `aCoords` are in its parent group's
   * plane; `getCoords()` applies the ancestor transform. `setCoords()` first, since the cached
   * plane coords go stale when typing relayouts move the textbox within its group.
   */
  const getTargetSceneTopLeft = (target: Textbox): CornerPoint => {
    target.setCoords();
    const [topLeft] = target.getCoords();
    return topLeft;
  };

  const getMenuPosition = (): PositionalParams => {
    const target = popupState.target as Textbox;
    const targetScaling = target.getObjectScaling();
    const angle = target.getTotalAngle();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore PMW added property.
    const pointer = target.getCharOffset(angle > 0 && angle < 90 ? popupState.wordPosition?.end : popupState.wordPosition?.start) as Record<string, number>;
    const {lineIndex} = calcLocation();
    const lineHeight = target.getHeightOfLine(lineIndex) * targetScaling.y;
    const lineTop = getTextboxLineTopOffset(target, lineIndex) * targetScaling.y;
    const posterScale = window.posterEditor?.whiteboard?.scaling.scale ?? 1;

    const targetTopLeft = getTargetSceneTopLeft(target);
    let posX = (targetTopLeft.x + pointer.x * targetScaling.x) * posterScale;
    // the spell-check popup hangs below the line; the selection popup sits on its top edge
    let posY = (targetTopLeft.y + lineTop + (popupState.openSpellCheckPopUpMenu ? lineHeight : 0)) * posterScale;
    if (angle) {
      const rotatedPosition = getPositionForRotatedTextbox(target, pointer);
      posX = rotatedPosition.x;
      posY = rotatedPosition.y;
    }
    const position = getPositionForCornercases(target, posX, posY);

    return {
      left: position.x,
      top: position.y + pageOffsetInPopupLayer - (popupState.openTextSelectionPopupMenu && !angle ? getDefaultPopupHeight() : 0),
    };
  };

  const getPositionForRotatedTextbox = (target: Textbox, pointer: Record<string, number>): CornerPoint => {
    const {lineIndex} = target.get2DCursorLocation();
    const targetScaling = target.getObjectScaling();
    const lineHeight = target.getHeightOfLine(lineIndex) * targetScaling.y;
    const angle = target.getTotalAngle();
    const radAngle = degreesToRadians(angle);
    const textAngle = 90 - angle;
    const textRadAngle = degreesToRadians(textAngle);
    const posterScale = window.posterEditor?.whiteboard?.scaling.scale ?? 1;

    // the distance along the textbox's own vertical axis to the line (plus half the line for the upper half-turn)
    const lineOffset = getTextboxLineTopOffset(target, lineIndex) * targetScaling.y + (angle < 180 ? lineHeight / 2 : 0);
    const targetTopLeft = getTargetSceneTopLeft(target);
    const left = targetTopLeft.x - lineOffset * Math.cos(textRadAngle);
    const top = targetTopLeft.y + lineOffset * Math.sin(textRadAngle);
    const posX = (left + pointer.x * targetScaling.x * Math.cos(radAngle)) * posterScale;
    let posY = (top + pointer.x * targetScaling.x * Math.sin(radAngle)) * posterScale;
    posY += angle >= 270 ? Math.abs(lineHeight * Math.cos(radAngle)) / 2 : 0;
    return {x: posX, y: posY};
  };

  const getDefaultPopupHeight = (): number => {
    if (popupState.openSpellCheckPopUpMenu) {
      return SUGGESTION_POPUP_HEIGHT;
    }
    if (popupState.openTextSelectionPopupMenu && isTextOptionExpanded) {
      return SELECTION_POPUP_EXPANDED_HEIGHT;
    }
    return SELECTION_POPUP_HEIGHT;
  };

  const getPositionForCornercases = (target: Textbox, posX: number, posY: number): CornerPoint => {
    const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
    if (canvas) {
      const posterScale = window.posterEditor?.whiteboard?.scaling.scale ?? 1;
      const {lineIndex} = calcLocation();
      const lineHeight = target.getHeightOfLine(lineIndex) * target.getObjectScaling().y * posterScale;
      const popupWidth = popUpRef.current?.offsetWidth ?? SUGGESTION_POPUP_MAX_WIDTH;
      const popupHeight = popUpRef.current?.offsetHeight ?? getDefaultPopupHeight();

      // posX/posY are in full-content (backdrop) space, which is also the popup's positioning context,
      // so the base position already tracks panning as the backdrop scrolls. The on-screen viewport
      // within that space is offset by the canvas pan (the virtualized renderer stores -scrollOffset in
      // viewportTransform 4/5) and sized to the on-screen canvas. Clamp to that window so the popup stays
      // visible when the canvas is zoomed and panned, instead of clamping to the full content bounds.
      const visibleLeft = -canvas.viewportTransform[4];
      const visibleTop = -canvas.viewportTransform[5];
      const visibleRight = visibleLeft + canvas.width;
      const visibleBottom = visibleTop + canvas.height;

      let newPosX = posX;
      let newPosY = posY;

      if (newPosX < visibleLeft + CORNER_PADDING) {
        newPosX = visibleLeft + CORNER_PADDING;
      }
      if (newPosY < visibleTop + CORNER_PADDING) {
        newPosY = visibleTop + CORNER_PADDING;
      }
      if (newPosX + popupWidth > visibleRight - CORNER_PADDING) {
        newPosX = visibleRight - popupWidth - CORNER_PADDING;
      }
      if (newPosY + popupHeight > visibleBottom - CORNER_PADDING) {
        newPosY -= lineHeight + popupHeight;
      }
      // Flipping above the line can push a tall popup past the top of the viewport; keep it on-screen.
      if (newPosY < visibleTop + CORNER_PADDING) {
        newPosY = visibleTop + CORNER_PADDING;
      }
      return {x: newPosX, y: newPosY};
    }
    return {x: posX, y: posY};
  };

  const calcLocation = (): IndexLocation => {
    const target = popupState.target as Textbox;
    const cursorLocation = target.get2DCursorLocation();
    const {lineIndex} = cursorLocation;
    let charIndex = cursorLocation.charIndex > 0 ? cursorLocation.charIndex - 1 : 0;
    charIndex += target.text.length >= target.selectionStart + 1 && target.searchWordBoundary(target.selectionStart + 1, -1) === target.selectionStart ? 1 : 0;
    return {lineIndex, charIndex};
  };

  const onSelectAllText = (): void => {
    const activeObject = activeCanvas?.getActiveObject();
    if (activeObject && activeObject instanceof Textbox) {
      activeObject.selectAll();
    }
    applyFocusOnTextarea();
    activeCanvas?.requestRenderAll();
  };

  const onCopyFromText = async (): Promise<void> => {
    const activeObject = activeCanvas?.getActiveObject();

    if (activeCanvas && activeObject && activeObject instanceof Textbox) {
      const selectionStart = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionStart : activeObject.selectionStart;
      const selectionEnd = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionEnd : activeObject.selectionEnd;
      await copyToClipboard(activeObject.text.slice(selectionStart, selectionEnd));
      activeObject.selectionStart = selectionEnd;
      activeObject.selectionEnd = selectionEnd;
    }
    applyFocusOnTextarea();
    closeTextSelectionPopup();
  };

  const onCutFromText = async (): Promise<void> => {
    if (activeItem?.isText() || activeItem?.isSlideshow()) {
      const editingTextbox = activeItem.getEditingTextbox();
      if (editingTextbox) {
        await copyToClipboard(editingTextbox.text.slice(editingTextbox.selectionStart, editingTextbox.selectionEnd));
        // Removal goes through fabric's editing API so the per-character styles map shifts with
        // the text, then the standard on-canvas change pipeline commits it.
        replaceTextboxSelectionWithText(editingTextbox, '');
        activeItem.onCanvasTextChange(editingTextbox.text);
      }
    }
    applyFocusOnTextarea();
    closeTextSelectionPopup();
  };

  const onPasteText = async (e?: React.MouseEvent<HTMLElement>): Promise<void> => {
    const activeObject = activeCanvas?.getActiveObject();

    if ((activeItem?.isText() || activeItem?.isSlideshow()) && activeCanvas && activeObject && activeObject instanceof Textbox) {
      await activeItem.onPasteText(e as unknown as Event);
    }
    applyFocusOnTextarea();
    closeTextSelectionPopup();
  };

  const getTextSelectionPopup = (): ReactElement<HTMLDivElement> => {
    return (
      <div className="flex-v-align-center">
        <div className="spacing-p-2 _fit-width flex-row-align-center">
          <Button text={window.i18next.t('pmwjs_cut')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onCutFromText} />
          <Button text={window.i18next.t('pmwjs_copy')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onCopyFromText} />
          <Button text={window.i18next.t('pmwjs_paste')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onPasteText} />
          <Icon
            icon="icon-menu-dots"
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST}
            shape={IconShape.SQUARE}
            isSelected={isTextOptionExpanded}
            onClick={(): void => {
              setIsTextOptionExpanded(!isTextOptionExpanded);
            }}
          />
        </div>
        {isTextOptionExpanded ? (
          <div className={`spacing-p-2 ${styles.textPopupMoreOptions}`}>
            <Button text={window.i18next.t('pmwjs_select_all')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onSelectAllText} />
          </div>
        ) : null}
      </div>
    );
  };

  const getTextNoSelectionPopup = (): ReactElement<HTMLDivElement> => {
    return (
      <div className="spacing-p-2 _fit-width flex-row-align-center">
        <Button text={window.i18next.t('pmwjs_select_all')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onSelectAllText} />
        <Button text={window.i18next.t('pmwjs_paste')} size={ButtonSize.SMALL} type={ButtonType.TERTIARY} onClick={onPasteText} />
      </div>
    );
  };

  const getSpellCheckSuggestionsPopup = (): ReactElement<HTMLUListElement> | null => {
    return (
      <ul className="popUpMenuList spacing-p-2 spacing-m-0">
        {getSuggestionsElement()}
        <li id="popup-btn-option" className="spacing-p-0 flex-h-row">
          <Button
            customClasses={`${styles.ignoreButton} flexbox flex-1 spacing-m-r-2`}
            textClasses={styles.ignoreText}
            type={ButtonType.TERTIARY}
            icon="icon-ban"
            text={window.i18next.t('pmwjs_ignore')}
            size={ButtonSize.LARGE}
            onClick={onIgnore}
          />
          <SpellCheckSettingsButton />
        </li>
      </ul>
    );
  };

  const getPopupMenu = (): ReactElement<HTMLElement> | null => {
    const activeObject = activeCanvas?.getActiveObject();
    if (popupState.openSpellCheckPopUpMenu) {
      return getSpellCheckSuggestionsPopup();
    }
    if (popupState.openTextSelectionPopupMenu) {
      if (activeObject && activeObject instanceof Textbox && activeObject.selectionStart === activeObject.selectionEnd) {
        return getTextNoSelectionPopup();
      }
      return getTextSelectionPopup();
    }
    return null;
  };

  return popupState.openSpellCheckPopUpMenu || popupState.openTextSelectionPopupMenu ? (
    <div ref={popupLayerRef} className={styles.popup}>
      <div ref={popUpRef} className={styles.popUpContainer} style={getMenuPosition()}>
        {getPopupMenu()}
      </div>
    </div>
  ) : null;
}
